import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react"
import { Snackbar, Alert } from "@mui/material"

const SnackbarContext = createContext()

export const useSnackbar = () => useContext(SnackbarContext)

// Global mutable function reference for non-React files
export let showSnackbarGlobal = () => {}

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info", // success | error | warning | info
  })

  // Stable reference so consumers that depend on showSnackbar (e.g. in useEffect
  // deps) don't re-run on every provider render — otherwise calling it triggers a
  // re-render, which yields a new reference, which re-fires the effect: a loop that
  // keeps re-opening the snackbar and resets its auto-hide timer forever.
  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }, [])

  // Register the global reference once on mount
  useEffect(() => {
    showSnackbarGlobal = showSnackbar
  }, [showSnackbar])

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar])

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}
